'use client';

import { FC, useMemo } from 'react';

import { RoleBasedToggle } from '@ravago/shared/page-data/models/config';
import { ElasticProduct } from '@ravago/shared/page-data/models/elastic';
import { ProductPortfolioNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { DataMapperUtils } from '@ravago/shared/page-data/utils/data-mapper';
import { CardGrid } from '@ravago/shared/radiance/components/card/components/CardGrid/CardGrid';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';

import { ProductCard } from '../../../../../shared/cards/product-card/product-card';

interface Props {
   baseRoutes?: {
      product: string;
      technology: string;
   };
   products: Array<ElasticProduct>;
   hasPublicLabelAndSigningsPermission: boolean;
   hasSustainabilityLabels: boolean;
   node: ProductPortfolioNode;
   globals: RendererGlobals;
   roleBasedToggles: RoleBasedToggle;
}

export const ProductGrid: FC<Props> = ({
   baseRoutes,
   products,
   hasPublicLabelAndSigningsPermission,
   roleBasedToggles,
   hasSustainabilityLabels,
   node,
   globals
}) => {
   const { locale, isConsumer, acceptWebp, assetBasePath } = globals;

   const { phone } = useBreakpoint();

   const { hasProductCompareFeatureToggle } = roleBasedToggles;

   const productCards = useMemo(
      () => products.map((product) => DataMapperUtils.normalizeElasticProductCard(product, assetBasePath, acceptWebp)),
      [assetBasePath, products, acceptWebp]
   );

   return (
      <CardGrid>
         {productCards?.map((product, productIndex) => (
            <ProductCard
               key={product.id}
               product={product}
               locale={locale}
               isConsumer={isConsumer}
               hasPublicLabelAndSigningsPermission={hasPublicLabelAndSigningsPermission}
               hasSustainabilityLabels={hasSustainabilityLabels}
               hasProductCompareFeatureToggle={hasProductCompareFeatureToggle}
               acceptWebp={acceptWebp}
               assetBasePath={assetBasePath}
               baseRoute={baseRoutes?.product}
               useStandardCardBanner={false}
               nodeType={node.type}
               testId={node.settings.testId}
               deferImage={phone ? productIndex > 1 : productIndex > 5}
            />
         ))}
      </CardGrid>
   );
};
